import closeIcon from '@app/assets/icons/close.svg';
import editIcon from '@app/assets/icons/edit.svg';
import saveIcon from '@app/assets/icons/save.svg';
import avatarImg from '@app/assets/images/user-placeholder.png';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { Upload } from '@app/components/common/Upload/Upload';
import { Entity } from '@app/constants/enums/entites';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { useLoader } from '@app/hooks/useLoader';
import { Fileprops } from '@app/interfaces/interfaces';
import { doUpdateUserProfile, doUploadProfilePicture } from '@app/store/slices/userSlice';
import type { UploadProps } from 'antd';
import { Avatar, Image, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'utils/utils';
import * as S from './ProfileInfo.styles';
import { IUser } from '@app/interfaces/user';
import { RcFile } from 'antd/lib/upload';

interface ProfileInfoProps {
  profileData: IUser | null;
}

export const ProfileInfo: React.FC<ProfileInfoProps> = ({ profileData }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [uploadImage, setUploadImage] = useState<string>('');
  const [profileImageFile, setProfileImageFile] = useState({});
  const [profileImageAction, setProfileImageAction] = useState(true);
  const { loader, handleLoaderOpen, handleLoaderClose } = useLoader();

  const props: UploadProps = {
    maxCount: 1,
    multiple: false,
    showUploadList: false,
    method: 'POST',
    accept: 'image/*',
    beforeUpload: (file) => {
      const isImage = file.type === 'image/*';
      return isImage;
    },
    onChange: (info) => {
      fileReaderHandler(info.file);
      setProfileImageFile(info.file);
    },
  };

  const fileReaderHandler = (file: Fileprops) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      setUploadImage(event.target?.result as string);
    };
    reader.readAsDataURL(file as Blob);
  };

  const UserImageFunctionality = () => {
    if (uploadImage === '') {
      return profileData?.image && profileData?.image?.signedURL ? profileData?.image?.signedURL : avatarImg;
    } else {
      return uploadImage;
    }
  };

  useEffect(() => {
    uploadImage === '' ? setProfileImageAction(false) : setProfileImageAction(true);
  }, [uploadImage]);

  const closeProfileImageHandler = () => {
    setUploadImage('');
    setProfileImageAction(false);
    setProfileImageFile({});
  };

  const saveProfileImageHandler = () => {
    if (profileData) {
      handleLoaderOpen();
      dispatch(
        doUploadProfilePicture({
          file: profileImageFile as RcFile,
          entityId: profileData.id,
          entity: Entity.USER,
        }),
      )
        .unwrap()
        .then(async (res) => {
          if (res) {
            const payload = {
              userDetails: {
                image: res.id,
              },
              userId: res.entityId || '',
            };
            await dispatch(doUpdateUserProfile(payload));
            handleLoaderClose();
            notificationController.success({
              message: t('alerts.successUpdateProfile'),
            });
          }
        })
        .catch((err) => {
          notificationController.error({ message: err.message });
        });
      setProfileImageAction(false);
    }
  };

  return profileData ? (
    <S.Wrapper>
      <Spinner spinning={loader}>
        <S.ImgWrapper>
          <Avatar shape="circle" src={UserImageFunctionality()} alt="Profile" />
          <Upload className="editIcon" {...props}>
            <Tooltip title={t('profile.nav.personalInfo.changeImage')} placement="top">
              <Image src={editIcon} alt="Edit" preview={false} />
            </Tooltip>
          </Upload>
        </S.ImgWrapper>
        {profileImageAction && (
          <S.ActionWrapper>
            <Image src={closeIcon} alt="Close" preview={false} onClick={closeProfileImageHandler} />
            <Image src={saveIcon} alt="Save" preview={false} onClick={saveProfileImageHandler} />
          </S.ActionWrapper>
        )}
        <S.Content>
          <S.Title>{`${capitalize(profileData?.firstname)} ${capitalize(profileData?.lastname)}`}</S.Title>
        </S.Content>
      </Spinner>
    </S.Wrapper>
  ) : null;
};
